import { Link, useParams } from "react-router";
import { useOrderDetail } from "@/lib/hooks";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import {
  formatCurrency,
  formatDate,
  formatOrderStatus,
  formatPaymentMethod,
  formatEmployeeStatus,
} from "@/lib/formatters";
import { ORDER_TYPE_LABELS } from "@/lib/api";
import type { Order, EmployeeStatus } from "@/lib/api";

function orderStatusBadgeClasses(status: Order["status"]): string {
  switch (status) {
    case "paid":
      return "bg-green-100 text-green-800";
    case "processing":
      return "bg-blue-100 text-blue-800";
    case "completed":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

function employeeStatusBadgeClasses(status: EmployeeStatus): string {
  switch (status) {
    case "attested_pending_verification":
      return "bg-yellow-100 text-yellow-800";
    case "attestation_not_found":
      return "bg-red-100 text-red-800";
    case "paid":
      return "bg-yellow-100 text-yellow-800";
    case "directed_to_bim":
      return "bg-orange-100 text-orange-800";
    case "fingerprint_appointment":
      return "bg-blue-100 text-blue-800";
    case "background_cleared":
      return "bg-green-100 text-green-800";
    case "entered_in_systems":
      return "bg-purple-100 text-purple-800";
    case "badge_issued":
      return "bg-teal-100 text-teal-800";
    case "badge_delivered":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function OrderDetailPage(): React.ReactElement {
  const { id } = useParams();
  const orderId = id ?? "";
  const { data, isLoading, error } = useOrderDetail(orderId);

  if (!id) {
    return <ErrorMessage message="Order not found" />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={
          error instanceof Error ? error.message : "Failed to load order"
        }
      />
    );
  }

  if (!data) {
    return <ErrorMessage message="Order not found" />;
  }

  const { order, employees } = data;

  return (
    <div className="mx-auto max-w-[800px]">
      <Link
        to="/orders"
        className="mb-4 inline-block text-sm text-blue-600 hover:text-blue-800"
      >
        &larr; Back to Orders
      </Link>

      <h1 className="mb-6 text-2xl font-bold text-gray-900">
        Order {order.id.slice(0, 8)}
      </h1>

      {/* Summary card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Order Summary
        </h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">Date</dt>
            <dd className="font-medium text-gray-900">
              {formatDate(order.createdAt)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Quantity</dt>
            <dd className="font-medium text-gray-900">
              {order.quantity} employee{order.quantity !== 1 ? "s" : ""}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Product</dt>
            <dd className="font-medium text-gray-900">
              {ORDER_TYPE_LABELS[order.type ?? "bcb"]}
            </dd>
          </div>
          {order.lines ? (
            // `lines` is authoritative: a mixed order's own unitPrice is 0.
            <div className="sm:col-span-2">
              <dt className="text-gray-500">Items</dt>
              <dd>
                <ul className="font-medium text-gray-900">
                  {order.lines.map((line) => (
                    <li key={line.product}>
                      {`${line.quantity} × ${ORDER_TYPE_LABELS[line.product]} — ${formatCurrency(line.quantity * line.unitPrice)}`}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : (
            <div>
              <dt className="text-gray-500">Unit Price</dt>
              <dd className="font-medium text-gray-900">
                {formatCurrency(order.unitPrice)}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-gray-500">Total</dt>
            <dd className="font-medium text-gray-900">
              {formatCurrency(order.totalAmount)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Payment</dt>
            <dd className="font-medium text-gray-900">
              {formatPaymentMethod(order.paymentMethod)} ending in{" "}
              {order.paymentMethodLast4}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Status</dt>
            <dd>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${orderStatusBadgeClasses(order.status)}`}
              >
                {formatOrderStatus(order.status)}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {/* Employee table */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Employees</h2>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-6 py-3 font-medium text-gray-700">Name</th>
              <th className="px-6 py-3 font-medium text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {employees.map((employee) => (
              <tr key={employee.id}>
                <td className="px-6 py-3 text-gray-900">
                  {employee.firstName} {employee.lastName}
                </td>
                <td className="px-6 py-3">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${employeeStatusBadgeClasses(employee.status)}`}
                  >
                    {formatEmployeeStatus(employee.status)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
